import { faCopy } from '@fortawesome/free-solid-svg-icons'
import {
  Button,
  IconButton,
  Label,
  LabelCheckbox,
  SelectDropDown,
  TextInput,
  Typography
} from 'procyon-ui'
import React, { useCallback } from 'react'
import { enqueueNotification } from 'Utils/Helpers'
import PrettyViewObject from 'Utils/PretteyViewObject'
import { FullScreenContentModal } from 'V2Components'

export default function StepContent({
  step,
  clusterName,
  setClusterName,
  hostIP,
  setHostIP,
  proxyType,
  setProxyType,
  isChecked,
  setIsChecked,
  user,
  apiKeys,
  selectedOS,
  setSelectedOS,
  showInstructionModal,
  setShowInstructionModal,
  error,
  setError
}) {
  const ipv4Regex =
    /^(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)$/

  let configObj = {
    Tenant: user.tenant,
    APIKey: apiKeys?.ApiKey,
    APISecret: apiKeys?.ApiSecret,
    CNTRLUrl: `https://${user.controller}`,
    SyncTimeMin: 60,
    ProxyImageTag: 'pinglatest',
    ProxyMode: 1,
    ClusterName: clusterName,
    HostIP: hostIP,
    NodeType: proxyType
  }

  if (isChecked) {
    configObj = {
      ...configObj,
      RDPMode: 1,
      RDPImageTag: 'pinglatest'
    }
  }

  const handleCopyConfig = useCallback(() => {
    const lines = [
      `Tenant=${user?.tenant}`,
      `APIKey=${apiKeys?.ApiKey}`,
      `APISecret=${apiKeys?.ApiSecret}`,
      `CNTRLUrl=https://${user?.controller}`,
      `SyncTimeMin=60`,
      `ProxyImageTag=pinglatest`,
      `ProxyMode=1`,
      `ClusterName=${clusterName}`,
      `HostIP=${hostIP}`,
      `NodeType=${proxyType}`
    ]

    if (isChecked) {
      lines.push(`RDPMode=1`)
      lines.push(`RDPImageTag=pinglatest`)
    }

    const textToCopy = lines.join('\n')

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        enqueueNotification(`Config copied to clipboard!`, 'info')
      })
      .catch((err) => {
        console.error('Could not copy text: ', err)
        enqueueNotification(`Failed to copy config`, 'error')
      })
  }, [user, apiKeys, clusterName, hostIP, proxyType, isChecked])

  const handleCopyCmd = (value) => {
    navigator.clipboard.writeText(value)
    enqueueNotification(`command copied to clipboard!`, 'info')
  }

  const getInstructionByOsType = (osType) => {
    const isRhel = osType === 'rhel'
    const isDebianAmd = osType === 'debian_amd'
    const isDebianArm = osType === 'debian_arm'

    const pkg = isRhel
      ? {
          title: 'RHEL / CentOS (RPM x86_64)',
          file: 'procyon-proxy-agent-x86_64.rpm',
          curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/proxy-guest-agent/rpm/procyon-proxy-agent-x86_64.rpm',
          wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/proxy-guest-agent/rpm/procyon-proxy-agent-x86_64.rpm',
          install: 'sudo rpm -ivh procyon-proxy-agent-x86_64.rpm'
        }
      : isDebianAmd
        ? {
            title: 'Debian / Ubuntu AMD64',
            file: 'procyon-proxy-agent_amd64.deb',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/proxy-guest-agent/debian/amd64/procyon-proxy-agent_amd64.deb',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/proxy-guest-agent/debian/amd64/procyon-proxy-agent_amd64.deb',
            install: 'sudo dpkg -i procyon-proxy-agent_amd64.deb'
          }
        : {
            title: 'Debian / Ubuntu ARM64',
            file: 'procyon-proxy-agent_arm64.deb',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/proxy-guest-agent/debian/arm64/procyon-proxy-agent_arm64.deb',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/proxy-guest-agent/debian/arm64/procyon-proxy-agent_arm64.deb',
            install: 'sudo dpkg -i procyon-proxy-agent_arm64.deb'
          }

    return (
      <>
        {/* Header */}
        <Typography variant='h5' className='mb-1'>
          Install Proxy Guest Agent
        </Typography>
        <Typography variant='body-regular' className='text-gray-600 mb-4'>
          {pkg.title} — follow the steps below on the gateway VM.
        </Typography>

        {/* STEP 1 */}
        <Typography variant='body-semiBold'>Step 1 — Download the package</Typography>
        <Typography variant='body-regular' className='text-gray-600 mb-2'>
          Choose <strong>one</strong> of the following methods:
        </Typography>

        <div className='relative'>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
            {pkg.curl}
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd(pkg.curl)}
            />
          </pre>
        </div>

        <Typography variant='body-semiBold' className='text-center my-2 text-gray-500'>
          — OR —
        </Typography>

        <div className='relative'>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
            {pkg.wget}
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd(pkg.wget)}
            />
          </pre>
        </div>

        {/* STEP 2 */}
        <div className='mt-4 relative'>
          <Typography variant='body-semiBold'>Step 2 — Install the package</Typography>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto mt-2'>
            {pkg.install}
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd(pkg.install)}
            />
          </pre>
        </div>

        {/* STEP 3 */}
        <div className='mt-4'>
          <Typography variant='body-semiBold'>Step 3 — Verify installation</Typography>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] rounded p-2 font-mono text-sm mt-2 relative'>
            systemctl status procyon-guest-agent
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2, top: 1 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd('systemctl status procyon-guest-agent')}
            />
          </pre>
          <Typography variant='body-regular' className='text-gray-600 mt-1'>
            Status should be <strong>active (running)</strong>
          </Typography>
        </div>

        {/* STEP 4 */}
        <div className='mt-4 rounded-md bg-green-50 border border-green-200 p-3'>
          <Typography variant='body-semiBold'>What happens next?</Typography>
          <ul className='list-disc pl-5 text-sm text-gray-700 mt-1'>
            <li>The agent connects to the controller</li>
            <li>The proxy container starts automatically</li>
            <li>The gateway appears in the UI within a few minutes</li>
          </ul>
        </div>
      </>
    )
  }

  const handleIPInputChange = (value) => {
    if (proxyType === 'Relay') {
      setHostIP(value)
    } else {
      setHostIP(value)
      if (value && !ipv4Regex.test(value)) {
        setError('Please enter a valid IPv4 address (e.g. 192.168.0.1)')
      } else {
        setError('')
      }
    }
  }

  const handleProxyTypeChange = (value) => {
    if (value !== proxyType) {
      setClusterName('')
      setHostIP('')
      setError('')
      setProxyType(value)
      setIsChecked(false)
    }
  }

  switch (step) {
    case 0:
      return (
        <div className='space-y-4'>
          <Typography variant='h3'>Create Virtual Machine</Typography>
          <Typography variant='body-regular'>
            Create a new Virtual Machine (VM) to deploy the proxy container. Ensure the VM meets the
            required specifications for optimal performance and connectivity.
          </Typography>

          <Typography variant='h5' className='font-semibold'>
            Minimum Required Specifications:
          </Typography>
          <ul className='list-disc pl-6 space-y-1 text-[15px] text-gray-700'>
            <li>
              <strong>CPU:</strong> 2 cores — sufficient for processing proxy operations
              efficiently.
            </li>
            <li>
              <strong>RAM:</strong> 4 GB — ensures smooth container operation.
            </li>
            <li>
              <strong>Storage:</strong> 20 GB — accommodates system files and application data.
            </li>
            <li>
              <strong>Firewall Policy:</strong>
              <ul className='list-disc pl-6'>
                <li>Outbound: Allow all traffic.</li>
                <li>
                  Inbound: If you create the VM for Private Proxy, open ports 8680 and 8690 for
                  application communication.
                </li>
              </ul>
            </li>
          </ul>

          <Typography variant='h5'>Supported OS:</Typography>

          <div className='flex flex-col md:flex-row gap-3 items-start'>
            {['RHEL / CentOS (RPM x86_64)', 'Debian / Ubuntu AMD64', 'Debian / Ubuntu ARM64'].map(
              (item) => (
                <Label text={item} />
              )
            )}
          </div>
        </div>
      )
    case 1:
      return (
        <div className='space-y-4'>
          <Typography variant='h3'>Install Docker and Verify</Typography>

          <Typography variant='body-regular'>
            Install Docker on the newly created VM and verify that it is running correctly. The
            installation method depends on the VM’s operating system.
          </Typography>

          <Typography variant='h5' className='font-semibold'>
            Instructions:
          </Typography>
          <ol className='list-decimal pl-6 space-y-3 text-[15px] text-gray-700'>
            <li>
              <strong>Install Docker:</strong>
              <ul className='list-disc pl-6'>
                <li>Follow the official Docker installation guide for your OS:</li>
                <li>
                  <a
                    href='https://docs.docker.com/engine/install/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-indigo-600 underline'
                  >
                    https://docs.docker.com/engine/install/
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <strong>Verify Docker is running:</strong>
              <div className='bg-gray-100 rounded-md p-3 mt-1 text-sm font-mono'>
                sudo docker ps
              </div>
              <p className='mt-1'>
                If the command executes without errors, Docker is installed and running
                successfully.
              </p>
            </li>
          </ol>
        </div>
      )
    case 2:
      return (
        <div className='space-y-5'>
          <Typography variant='body-semiBold' className='text-gray-600'>
            Choose how this gateway connects to your environment.
          </Typography>

          <div className='flex gap-4'>
            {[
              {
                label: 'Private Proxy',
                value: 'PrivateProxy',
                desc: 'Standard Gateway (accepts incoming connections).'
              },
              {
                label: 'Relay',
                value: 'Relay',
                desc: 'Restricted outbound-only gateway (no inbound open ports).'
              }
            ].map((opt) => (
              <div
                key={opt.value}
                onClick={() => handleProxyTypeChange(opt.value)}
                className={`cursor-pointer rounded-lg border px-4 py-3 transition-all ${
                  proxyType === opt.value
                    ? 'border-[var(--color-button-primary-border)] bg-[var(--color-button-primary-bg)]'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div
                  className={`font-bold ${proxyType === opt.value ? 'text-[#ffff]' : 'text-[var(--color-font)]'}`}
                >
                  {opt.label}
                </div>
                <div
                  className={`font-sm ${proxyType === opt.value ? 'text-[#ffff]' : 'text-[var(--color-font)]'}`}
                >
                  {opt.desc}
                </div>
              </div>
            ))}
          </div>

          {proxyType === 'Relay' && (
            <div className='flex align-center mt-2'>
              <Typography variant='body-semiBold'>Requirement:</Typography>
              <Typography variant='body-regular' className='ml-1'>
                At least one PrivateProxy must exist to use Relay
              </Typography>
            </div>
          )}

          <TextInput
            sx={{ width: '100%' }}
            fullWidth
            value={clusterName}
            onChange={(e) => setClusterName(typeof e === 'string' ? e : (e?.target?.value ?? ''))}
            label={proxyType === 'Relay' ? 'Relay Name *' : 'Proxy Cluster Name *'}
          />

          <TextInput
            sx={{ width: '100%' }}
            fullWidth
            value={hostIP}
            onChange={(e) => handleIPInputChange(e.target.value)}
            label={(proxyType === 'Relay' ? 'Host Name' : 'FQDN / Host IP') + ' *'}
          />

          <div className='flex items-center, justify-between'>
            <LabelCheckbox
              checked={isChecked}
              name='Deploy RDP'
              onClick={() => setIsChecked((prev) => !prev)}
            />
            {error === '' ? '' : <Label text={error} variant='warning' />}
          </div>
        </div>
      )
    case 3:
      return (
        <div>
          <Typography variant='h3'>Create Env File</Typography>
          <div className='space-y-2 my-4'>
            <Typography variant='body-semiBold'>
              Create the directory (if not already present):
            </Typography>
            <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
              {'sudo mkdir -p /var/lib/procyon'}
              <IconButton
                style={{ width: 20 }}
                icon={faCopy}
                variant='gray'
                onClick={() => handleCopyCmd('sudo mkdir -p /var/lib/procyon')}
              />
            </pre>
            <Typography variant='body-semiBold'>Create the file and open it:</Typography>
            <pre className='flex items-center justify-between bg-[#1e1e1e] text-[#dcdcdc] rounded p-2 font-mono text-sm overflow-x-auto'>
              {'sudo vi /var/lib/procyon/procyon-guest-agent.env'}
              <IconButton
                style={{ width: 20 }}
                icon={faCopy}
                variant='gray'
                onClick={() => handleCopyCmd('sudo vi /var/lib/procyon/procyon-guest-agent.env')}
              />
            </pre>
          </div>
          <div className='space-y-1 mt-5'>
            <Typography variant='body-regular'>
              Paste the below content inside the file & save it.
            </Typography>
            <div className='relative'>
              <PrettyViewObject data={configObj} />
              <IconButton
                style={{ width: 20, position: 'absolute', bottom: 10, right: 10 }}
                icon={faCopy}
                variant='gray'
                onClick={() => handleCopyConfig()}
              />
            </div>
          </div>
        </div>
      )
    case 4:
      return (
        <div className='space-y-4'>
          <Typography variant='h3'>Install Proxy Guest Agent</Typography>
          <Typography variant='body-regular'>
            Download and install the proxy guest agent based on your operating system and
            architecture. The service will start automatically after installation, bringing up the
            proxy container.
          </Typography>

          <div className='space-y-2'>
            <Typography variant='h5' className='font-semibold'>
              Download for your OS:
            </Typography>

            <div className='flex flex-col md:flex-row gap-3 items-start'>
              <SelectDropDown
                menuItems={[
                  { label: 'Select your OS / Architecture', value: '', disabled: true },
                  {
                    label: 'RHEL / CentOS (RPM x86_64)',
                    value: 'rhel',
                    selected: selectedOS === 'rhel'
                  },
                  {
                    label: 'Debian / Ubuntu AMD64',
                    value: 'debian_amd',
                    selected: selectedOS === 'debian_amd'
                  },
                  {
                    label: 'Debian / Ubuntu ARM64',
                    value: 'debian_arm',
                    selected: selectedOS === 'debian_arm'
                  }
                ]}
                value={selectedOS}
                onChange={(val) => setSelectedOS(val?.target?.value ?? val)}
              />

              {selectedOS && (
                <>
                  <Button variant='grayBlue' onClick={() => setShowInstructionModal(selectedOS)}>
                    Show Instructions
                  </Button>
                </>
              )}
            </div>
          </div>
          {showInstructionModal && (
            <FullScreenContentModal
              children={
                <>
                  {getInstructionByOsType(selectedOS)}
                  <div className='mt-4 flex justify-end gap-4'>
                    <Button
                      variant='gray'
                      onClick={() => {
                        setShowInstructionModal(!showInstructionModal)
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </>
              }
            />
          )}
        </div>
      )

    default:
      return null
  }
}
